import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bookingApprovalsApi } from "@/lib/api/booking-approvals";
import { approvalPage } from "@/lib/booking-approvals";
import { RECONNECT_EVENT, usePortalEvent } from "@/lib/portal-socket";

export const BOOKING_APPROVALS_KEY = ["booking-approvals"] as const;
export const APPROVAL_SETTINGS_KEY = ["booking-approval-settings"] as const;

/** The company's approval rule (any member may read it; the booking dialogs warn with it). */
export function useApprovalSettings() {
  return useQuery({
    queryKey: APPROVAL_SETTINGS_KEY,
    queryFn: () => bookingApprovalsApi.getSettings().then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

/** The company's open booking requests (a first page plus the ``total``, #434) — only for whoever may decide them: the settings' server-computed
 *  ``can_edit`` (a company admin, or staff — plan R5), never the portal's staff-exclusive ``is_tenant_admin``.
 *  Nobody else fetches. A slow poll is the safety net; the pushes are handled by ``useBookingApprovalCount``. */
export function useBookingApprovals() {
  const isAdmin = useApprovalSettings().data?.can_edit === true;
  const query = useQuery({
    queryKey: BOOKING_APPROVALS_KEY,
    queryFn: () => bookingApprovalsApi.list("pending").then((r) => approvalPage(r.data, r.headers)),
    enabled: isAdmin,
    refetchInterval: 60_000,
    staleTime: 0,
  });
  return { ...query, isAdmin };
}

/** Open requests, all of them (not just the first page) — the Meetings nav badge. Mounted once, in the app shell, so it is also the one place that
 *  refetches on the `booking_approvals_changed` push and on reconnect (a second subscriber would only refresh twice). */
export function useBookingApprovalCount(): number {
  const qc = useQueryClient();
  const { data } = useBookingApprovals();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: BOOKING_APPROVALS_KEY });
    qc.invalidateQueries({ queryKey: ["my-bookings"] });
  };
  usePortalEvent("booking_approvals_changed", refresh);
  usePortalEvent(RECONNECT_EVENT, refresh);
  return data?.total ?? 0;
}
